import "./TechDrawerView.css"
import { useRef } from "react"
import { Drawer, DrawerRef } from "../drawers/Drawer"
import Icon from "../base/Icon"
import Job from "../../modules/crm/jobs/models/Job"
import Technician from "../../modules/technicians/models/technician"
import TechPhoneStatusCard from "./TechPhoneStatusCard"
import { techMarkerHtml } from "../map/TechMarker"
import { getUser, hasRole, openJobQuickView, useGlobal, useTechsAll } from "../../core/Global"
import { canPaintJob, jobColorOf, jobColorVars } from "../../modules/crm/jobs/components/JobColor"
import { useTechLive } from "../../core/TechLive"
import JobColorPicker from "../../modules/crm/jobs/components/JobColorPicker"

interface IProps {
    id: string
    onClose: () => void
    // Offered when the drawer was opened to pick somebody -- from the Metro & Technician
    // drawer's map -- as a "Select this technician" button under their details.
    onSelect?: (tech: Technician) => void
}

/**
 * A technician, opened from their pin on the map.
 *
 * The hover panel is deliberately small — every technician's reads the same height, so a map
 * full of them stays a map rather than a wall of cards. Everything that would have made it grow
 * lives here instead, where there is room for it and where a job can be opened on top as its
 * own drawer.
 */
export default function TechDrawerView(props: IProps) {

    const [globalState] = useGlobal()
    const user = getUser()

    // Closing goes through the drawer's own script, never by unmounting it: that is what plays
    // the slide-out and takes the overlay down with it. onClose follows once it is hidden.
    const drawerRef = useRef<DrawerRef>(null)

    const bankTech = useTechsAll().find(t => t.id === props.id)

    // Only this one technician is subscribed to, so somebody else moving across town does not
    // re-render the panel being read.
    const live = useTechLive(props.id)

    const tech = bankTech && live
        ? { ...bankTech, techLocation: live.position, techLocationLastUpdate: live.at }
        : bankTech

    const jobs = order((globalState.jobs ?? []).filter(job => job.techId === props.id))

    const inProgress = jobs.filter(job => job.isInProgress).length

    return (
        <Drawer
            ref={drawerRef}
            id="drawer-tech-view"
            // Their picture and name are the drawer's title, so the name is said once and the
            // room it took below goes to the cards.
            title={tech
                ? <div className="d-flex align-items-center" style={{ minWidth: 0 }}>
                    <div
                        className="me-3 flex-shrink-0"
                        style={{ width: 44, height: 44 }}
                        dangerouslySetInnerHTML={{ __html: techMarkerHtml(tech, { size: 44, plain: true }) }}
                    />
                    <div style={{ minWidth: 0 }}>
                        <div className="fw-bold fs-3 text-truncate lh-sm">{tech.fullName}</div>
                        {tech.username && <div className="text-muted fs-7 fw-normal text-truncate">@{tech.username}</div>}
                    </div>
                </div>
                : "Technician"}
            groupedBg
            // Only the list of jobs scrolls — see below. The drawer must not scroll as a whole,
            // or who they are scrolls away with it.
            noScroll
            // The job opened from here covers this one; most of it still shows beside it, so the
            // list you were reading stays where you left it.
            stackReveal
            onClose={props.onClose}
        >
            {!tech
                ? <div className="p-10 text-center text-gray-500">This technician is no longer on the board.</div>
                : <div className="d-flex flex-column" style={{ flex: "1 1 auto", minHeight: 0 }}>

                    {/* What their phone says about itself, under one "Updated". */}
                    <TechPhoneStatusCard techId={tech.id} locationAt={tech.techLocation ? tech.techLocationLastUpdate : undefined} />

                    {/* How to reach them — nothing to do with the phone's state. */}
                    {(tech.phoneItem?.phoneFriendly || tech.email) &&
                        <div className="tech-card mb-5">
                            <div className="tech-card-label mb-3">Details</div>

                            {tech.phoneItem?.phoneFriendly &&
                                <Row icon="phone">{tech.phoneItem.phoneFriendly}</Row>
                            }

                            {tech.email && <Row icon="envelope">{tech.email}</Row>}
                        </div>
                    }

                    {/* Above their jobs, below who they are: the reason this drawer was opened,
                        when it was opened to pick somebody. */}
                    {props.onSelect &&
                        <button type="button" className="btn btn-primary w-100 mb-5 fw-bold" onClick={() => { props.onSelect?.(tech); drawerRef.current?.close() }}>
                            <Icon icon="user-check" type="solid" size={14} className="me-2" />
                            Select this technician
                        </button>
                    }

                    <div className="d-flex align-items-center justify-content-between mb-3">
                        <div className="fw-bold fs-5">
                            {jobs.length === 0
                                ? "No jobs on the board"
                                : `${jobs.length} job${jobs.length === 1 ? "" : "s"}`}
                        </div>
                        {inProgress > 0 &&
                            <div className="fs-8 fw-bold text-danger">
                                <Icon icon="briefcase" type="solid" className="me-1" size={11} />
                                {inProgress} in progress
                            </div>
                        }
                    </div>

                    {/* The only part that scrolls. Everything above it is who they are, and
                        that has to stay on screen while their jobs are read. */}
                    <div className="scroll-y" style={{ flex: "1 1 auto", minHeight: 0, overflowY: "auto", marginRight: -6, paddingRight: 6 }}>
                    {jobs.map(job => {
                        // The colour a dispatcher painted on the board, said here the same way
                        // the board says it: a band down the left edge and a tint that fades.
                        const jobColor = jobColorOf(job, user?.isTech)

                        return (
                        <div
                            key={job.id}
                            className={`d-flex align-items-center p-3 mb-2 rounded cursor-pointer${jobColor ? " job-colored job-colored-card" : ""}`}
                            style={{
                                // backgroundColor, not the `background` shorthand: the shorthand
                                // would wipe out the tint `.job-colored` paints as a background
                                // image and leave the band standing on its own.
                                backgroundColor: "var(--kt-body-bg, #fff)",
                                paddingLeft: jobColor ? 20 : undefined,
                                ...jobColorVars(jobColor)
                            }}
                            onClick={() => openJobQuickView(job.id)}
                        >
                            {jobColor &&
                                (canPaintJob(job, user?.isTech, hasRole("EditArchivedJobs"))
                                    ? <JobColorPicker job={job} variant="spine" />
                                    : <span className="color-selector-spine is-static" style={{ backgroundColor: jobColor }} />)
                            }

                            <span
                                className="d-inline-block rounded-circle me-3 flex-shrink-0"
                                style={{ width: 10, height: 10, background: job.jobStatus?.color ?? "#b5b5c3" }}
                            />
                            <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                <div className="fs-7 fw-bold text-gray-800 text-truncate">
                                    #{job.jobId}
                                    {job.jobStatus?.name &&
                                        <span className="fw-normal text-gray-600"> · {job.jobStatus.name}</span>
                                    }
                                </div>
                                <div className="fs-8 text-gray-700 text-truncate">
                                    {job.fullName ?? "-"}
                                    {job.jobTypeWithGroup && <> · {job.jobTypeWithGroup}</>}
                                </div>
                                <div className="fs-8 text-gray-500 text-truncate">
                                    {job.address?.address?.label ?? job.address?.title ?? "-"}
                                </div>
                            </div>
                            <Icon icon="chevron-right" type="solid" size={12} className="text-gray-400 ms-3" />
                        </div>
                        )
                    })}
                    </div>
                </div>
            }
        </Drawer>
    )
}

function Row(props: { icon: any, children: any, iconClassName?: string }) {
    return (
        <div className="tech-detail-row d-flex align-items-center fs-7 text-gray-800">
            <Icon icon={props.icon} type="regular" size={14} className={`me-3 ${props.iconClassName ?? "text-gray-500"}`} />
            <div className="text-truncate">{props.children}</div>
        </div>
    )
}

/** What they are doing now, then what is waiting — the order the question is asked in. */
function order(jobs: Job[]): Job[] {
    return [...jobs].sort((a, b) => {
        const weight = (job: Job) => (job.isInProgress ? 0 : job.appointmentFrom ? 1 : 2)
        const difference = weight(a) - weight(b)

        if (difference !== 0) return difference

        return (a.createdDateSortNumber ?? 0) - (b.createdDateSortNumber ?? 0)
    })
}
